import React, { useEffect, useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { fetchStaffTeam, inviteStaffMember, resendStaffSetupLink, type StaffMember } from '../../utils/staffTeam';
import type { StaffRole } from '../../utils/staffAuth';
import { BUTTON_GHOST, BUTTON_QUIET, BUTTON_SOLID, FIELD, OPTION, SELECT } from '../../components/portal/adminStyles';

/**
 * Portal Productions staff accounts. Owners only — the list and the invitation
 * both come from the staff-team Edge Function, which re-checks the role
 * server-side, so hiding the page is presentation rather than protection.
 */
export function Team() {
  const {
    staffUser,
    notify,
    goPortal
  } = usePortal();
  const [members, setMembers] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<StaffRole>('staff');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState('');
  async function load() {
    setLoading(true);
    const result = await fetchStaffTeam();
    if (!result.ok) setLoadError(result.error ?? 'The team could not be loaded.');else {
      setLoadError('');
      setMembers(result.members ?? []);
    }
    setLoading(false);
  }
  useEffect(() => {
    void load();
  }, []);
  async function resend(address: string) {
    if (resending) return;
    setResending(address);
    const result = await resendStaffSetupLink(address);
    setResending('');
    notify(result.ok ? `New setup link sent to ${address}` : 'Setup link could not be sent.');
  }
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    const address = email.trim().toLowerCase();
    const result = await inviteStaffMember(address, role);
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'Invitation could not be sent.');
      return;
    }
    notify(`Invitation sent to ${address}`);
    setEmail('');
    setRole('staff');
    setOpen(false);
    void load();
  }
  if (staffUser?.role !== 'owner') {
    return <section aria-labelledby="team" data-id="element-861" data-mp="2sa4shefm2ypz">
        <h1 id="team" className="font-display text-4xl leading-none tracking-tight sm:text-5xl" data-id="element-862" data-mp="2sb669qo0hzbe">
          Team
        </h1>
        <p className="mt-8 font-display text-xl text-paper/40" data-id="element-863" data-mp="3pv0taoigzjb8">
          Only Portal owners can manage staff accounts.
        </p>
        <p className="label-sm mt-5 text-paper/30" data-id="element-864" data-mp="3fynzzrqu77h0">
          Everything else in Portal is the same for you as for an owner.
        </p>
      </section>;
  }
  return <section aria-labelledby="team" data-id="element-865" data-mp="3b2gqjoz8fdub">
      <button type="button" onClick={() => goPortal('projects')} className="label text-paper/35 transition-colors duration-150 ease-out hover:text-paper" data-id="element-866" data-mp="24kag0xz7fc69">
        ← Client Projects
      </button>

      <h1 id="team" className="mt-10 font-display text-4xl leading-none tracking-tight sm:text-5xl" data-id="element-867" data-mp="f4hoph7inaxm">
        Team
      </h1>
      <p className="label-sm mt-6 text-paper/40" data-id="element-868" data-mp="p1ckvtgbgz8q">Portal Productions staff accounts.</p>

      <ul className="m-0 mt-14 list-none p-0" data-id="element-869" data-mp="trswr94hocsz">
        {loading && <li className="border-b border-ruleDark py-8 font-display text-xl text-paper/40" data-id="element-870" data-mp="tt42vflepwn9">
            Loading team…
          </li>}

        {!loading && loadError && <li className="border-b border-ruleDark py-8 font-display text-xl text-paper/40" data-id="element-871" data-mp="12r67040ft5er">
            {loadError}
          </li>}

        {!loading && !loadError && members.map(member => <li key={member.userId} className="flex flex-wrap items-baseline justify-between gap-x-8 gap-y-3 border-b border-ruleDark py-7" data-id="element-872" data-mp="1coknu4q9vw7l">
              <div className="min-w-0" data-id="element-873" data-mp="3eijweic0fzgl">
                <p className="m-0 font-display text-2xl leading-none" data-id="element-874" data-mp="18thmo7w91bgm">{member.name}</p>
                <p className="label-sm mt-3 text-paper/30" data-id="element-875" data-mp="18vkjpbvtk4uu">{member.email}</p>
              </div>
              <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2" data-id="element-876" data-mp="13x0xdadbcr1y">
                {member.pending && <>
                    <span className="label-sm text-paper/30" data-id="element-877" data-mp="1dsktnxvshrr8">Invited</span>
                    {/* Presentation only — the function re-checks the role. */}
                    <button type="button" onClick={() => resend(member.email)} disabled={resending === member.email} className="label-sm text-paper/35 underline underline-offset-4 transition-colors duration-150 ease-out hover:text-paper disabled:text-paper/20 disabled:hover:text-paper/20" data-id="element-878" data-mp="q0vtjvcq7yzq">
                      Resend Setup Link
                    </button>
                  </>}
                <span className="label text-paper/50" data-id="element-879" data-mp="3of4w1g8jr26p">{member.role}</span>
              </div>
            </li>)}
      </ul>

      {open ? <form onSubmit={submit} className="mt-12 max-w-xl" data-id="element-880" data-mp="3t6mx04akl2ro">
          <p className="label m-0 text-paper/40" data-id="element-881" data-mp="3eh77ssqot5e3">Invite Team Member</p>

          <label className="mt-9 block" data-id="element-882" data-mp="3eeuitvmihurj">
            <span className="label block text-paper/40" data-id="element-883" data-mp="3jfifawtif3t5">Email</span>
            <input type="email" value={email} onChange={event => setEmail(event.target.value)} placeholder="e.g. name@portalproductions.me" autoFocus className={FIELD} data-id="element-884" data-mp="3jdp7vfb2ofk3" />
          </label>

          <label className="mt-9 block" data-id="element-885" data-mp="145604fwv9rk">
            <span className="label block text-paper/40" data-id="element-886" data-mp="60m4nskql6iw">Role</span>
            <select value={role} onChange={event => setRole(event.target.value as StaffRole)} className={SELECT} data-id="element-887" data-mp="62yokba21qyl">
              <option value="staff" className={OPTION} data-id="element-888" data-mp="2qsxopykuyvqn">
                Staff
              </option>
              <option value="owner" className={OPTION} data-id="element-889" data-mp="x7ugm9dnl62a">
                Owner
              </option>
            </select>
          </label>

          {error && <p role="alert" className="label-sm mt-8 text-paper" data-id="element-890" data-mp="ni2hr2yitqs0">
              {error}
            </p>}

          <div className="mt-10 flex flex-wrap items-center gap-6" data-id="element-891" data-mp="iiyawiz2k54u">
            <button type="submit" disabled={busy} className={BUTTON_SOLID} data-id="element-892" data-mp="dl67ossz4wlc">
              Send Invitation
            </button>
            <button type="button" onClick={() => setOpen(false)} className={BUTTON_QUIET} data-id="element-893" data-mp="9i08g54a45od">
              Cancel
            </button>
          </div>
        </form> : <button type="button" onClick={() => setOpen(true)} className={`${BUTTON_GHOST} mt-12 inline-flex items-center gap-3`} data-id="element-894" data-mp="3vqsym5bpoh52">
          <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-895" data-mp="1yvn69rwnaizx" />
          Invite Team Member
        </button>}
    </section>;
}